import { formatNumber } from '../../charts/formatters'

/** Four equal-frequency bands on non-zero counts (distribution-based thresholds for the period). */
export const VOLUME_BAND_COUNT = 4

export const BAND_COLORS = {
  0: 'transparent',
  1: '#f1f5f9',
  2: '#ddd6fe',
  3: '#8b5cf6',
  4: '#4c1d95',
}

export const BAND_LABELS = ['Low', 'Moderate', 'High', 'Peak']

export const FILTER_INACTIVE_GREY = '#E5E7EB'

/** Equal-frequency cut points between non-zero message counts (length = VOLUME_BAND_COUNT - 1). */
export function computeVolumeBandEdges(matrix) {
  const flat = matrix.flat().filter((v) => v > 0)
  if (flat.length === 0) return { edges: [], bandCount: VOLUME_BAND_COUNT }
  const sorted = [...flat].sort((a, b) => a - b)
  const n = sorted.length
  const edges = []
  for (let b = 1; b < VOLUME_BAND_COUNT; b += 1) {
    const idx = Math.floor((n * b) / VOLUME_BAND_COUNT)
    edges.push(sorted[Math.min(Math.max(0, idx), n - 1)])
  }
  return { edges, bandCount: VOLUME_BAND_COUNT }
}

export function getVolumeBand(value, edges, bandCount) {
  if (value <= 0) return 0
  if (!edges.length) return Math.min(bandCount, Math.max(1, Math.ceil(bandCount / 2)))
  for (let i = 0; i < edges.length; i += 1) {
    if (value <= edges[i]) return i + 1
  }
  return bandCount
}

export function messageVolumeTierTooltipPhrase(level) {
  if (level >= 1 && level <= VOLUME_BAND_COUNT) return `${BAND_LABELS[level - 1]} volume`
  return ''
}

export function messageVolumeTierLabel(level) {
  if (level >= 1 && level <= VOLUME_BAND_COUNT) return BAND_LABELS[level - 1]
  return ''
}

export function fmtMsgThreshold(v) {
  return formatNumber(Math.max(0, Math.round(Number(v) || 0)))
}

export function buildMessageLegendTierRows(edges, bandCount) {
  const e = edges.map((x) => Math.max(0, Math.round(Number(x) || 0)))
  const rows = []
  for (let level = 1; level <= bandCount; level += 1) {
    const name = BAND_LABELS[level - 1]
    let range = '—'
    if (level === 1 && e[0] != null) range = `Up to ${fmtMsgThreshold(e[0])} messages`
    else if (level > 1 && level < bandCount && e[level - 2] != null && e[level - 1] != null) {
      range = `${fmtMsgThreshold(e[level - 2])}–${fmtMsgThreshold(e[level - 1])} messages`
    } else if (level === bandCount && e[bandCount - 2] != null) {
      range = `${fmtMsgThreshold(e[bandCount - 2])} or more messages`
    }
    rows.push({ level, name, range })
  }
  return rows
}

export function computeBandSummaries(matrix, edges, bandCount, grandTotal) {
  return Array.from({ length: bandCount }, (_, i) => i + 1).map((level) => {
    let slotCount = 0
    let totalMessages = 0
    for (let row = 0; row < matrix.length; row += 1) {
      const rowVals = matrix[row]
      if (!rowVals) continue
      for (let col = 0; col < rowVals.length; col += 1) {
        const v = Number(rowVals[col] ?? 0)
        if (v <= 0) continue
        if (getVolumeBand(v, edges, bandCount) !== level) continue
        slotCount += 1
        totalMessages += v
      }
    }
    const share = grandTotal > 0 ? totalMessages / grandTotal : 0
    return { level, slotCount, totalMessages, share }
  })
}

export function segmentBackgroundColor(level, pinnedBand) {
  if (level === 0) return 'transparent'
  if (pinnedBand == null) return BAND_COLORS[level] ?? BAND_COLORS[0]
  if (level === pinnedBand) return BAND_COLORS[level] ?? BAND_COLORS[0]
  return FILTER_INACTIVE_GREY
}

export function legendSwatchColor(bandLevel, pinnedBand) {
  if (pinnedBand == null) return BAND_COLORS[bandLevel] ?? BAND_COLORS[0]
  if (bandLevel === pinnedBand) return BAND_COLORS[bandLevel] ?? BAND_COLORS[0]
  return FILTER_INACTIVE_GREY
}
